import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FileUp, MessageSquare, Film } from "lucide-react";
import api from "../../lib/api";
import { StatusMark } from "../../components/StatusMark";
import { formatDate, formatApiErrorDetail } from "../../lib/format";

const KINDS = [
  { value: "question", label: "Question" },
  { value: "attendance", label: "Attendance" },
  { value: "request", label: "Request" },
  { value: "note", label: "Note" },
];

export default function PortalRequests() {
  const [classes, setClasses] = useState([]);
  const [subs, setSubs] = useState([]);
  const [msgs, setMsgs] = useState([]);

  // Submit a scene
  const [sceneTitle, setSceneTitle] = useState("");
  const [sceneClass, setSceneClass] = useState("");
  const [sceneNote, setSceneNote] = useState("");
  const [sceneFile, setSceneFile] = useState(null);
  const [sceneBusy, setSceneBusy] = useState(false);

  // Ask & update
  const [kind, setKind] = useState("question");
  const [msgClass, setMsgClass] = useState("");
  const [msgBody, setMsgBody] = useState("");
  const [msgBusy, setMsgBusy] = useState(false);

  const load = useCallback(async () => {
    const [c, s, m] = await Promise.all([
      api.get("/classes"),
      api.get("/submissions/mine"),
      api.get("/messages/mine"),
    ]);
    setClasses(c.data);
    setSubs(s.data);
    setMsgs(m.data);
  }, []);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const submitScene = async (e) => {
    e.preventDefault();
    setSceneBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", sceneTitle);
      fd.append("note", sceneNote);
      if (sceneClass) fd.append("class_id", sceneClass);
      if (sceneFile) fd.append("file", sceneFile);
      await api.post("/submissions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sent to the studio for review.");
      setSceneTitle(""); setSceneNote(""); setSceneClass(""); setSceneFile(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSceneBusy(false);
    }
  };

  const submitMsg = async (e) => {
    e.preventDefault();
    setMsgBusy(true);
    try {
      await api.post("/messages", { kind, body: msgBody, class_id: msgClass || null });
      toast.success("Sent. The studio will pick it up — no need to wait on a reply.");
      setMsgBody(""); setMsgClass(""); setKind("question");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setMsgBusy(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="portal-requests">
      <div>
        <p className="overline mb-2">Your channel to the studio</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">Requests & Questions</h1>
        <p className="font-sans text-sm text-stone mt-2 max-w-xl">
          Propose a scene you want to work, flag an absence, or ask a quick question. It goes straight to the studio —
          you won't always get a reply, but it's always seen.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Submit a scene */}
        <form onSubmit={submitScene} className="paper-card p-7 space-y-4" data-testid="submit-scene-form">
          <div className="flex items-center gap-2">
            <Film size={18} className="text-brass" />
            <h2 className="font-serif text-2xl text-ink">Submit a scene</h2>
          </div>
          <div>
            <label className="field-label">Scene / title</label>
            <input className="field-input" value={sceneTitle} onChange={(e) => setSceneTitle(e.target.value)} required placeholder="e.g. Closer — the bar scene" data-testid="scene-title" />
          </div>
          <div>
            <label className="field-label">For a class (optional)</label>
            <select className="field-input" value={sceneClass} onChange={(e) => setSceneClass(e.target.value)} data-testid="scene-class">
              <option value="">No specific class</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label">Note (why this scene?)</label>
            <textarea className="field-input" rows={2} value={sceneNote} onChange={(e) => setSceneNote(e.target.value)} placeholder="Cold read, two-hander, want to push range…" data-testid="scene-note" />
          </div>
          <div>
            <label className="field-label">Attach sides PDF (optional)</label>
            <input type="file" accept="application/pdf" onChange={(e) => setSceneFile(e.target.files[0])} className="field-input" data-testid="scene-file" />
          </div>
          <button type="submit" disabled={sceneBusy} className="btn-brass text-sm" data-testid="scene-submit">
            <FileUp size={15} /> {sceneBusy ? "Sending…" : "Send to studio"}
          </button>
        </form>

        {/* Ask & update */}
        <form onSubmit={submitMsg} className="paper-card p-7 space-y-4" data-testid="ask-form">
          <div className="flex items-center gap-2">
            <MessageSquare size={18} className="text-brass" />
            <h2 className="font-serif text-2xl text-ink">Ask & update</h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="field-label">Type</label>
              <select className="field-input" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="msg-kind">
                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Class (optional)</label>
              <select className="field-input" value={msgClass} onChange={(e) => setMsgClass(e.target.value)} data-testid="msg-class">
                <option value="">—</option>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="field-label">Message</label>
            <textarea className="field-input" rows={3} value={msgBody} onChange={(e) => setMsgBody(e.target.value)} required placeholder="I'll miss the top of Thursday — booked a self-tape…" data-testid="msg-body" />
          </div>
          <button type="submit" disabled={msgBusy} className="btn-brass text-sm" data-testid="msg-submit">
            <MessageSquare size={15} /> {msgBusy ? "Sending…" : "Send"}
          </button>
        </form>
      </div>

      {/* Feed */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h3 className="font-serif text-2xl text-ink mb-4">My scene proposals</h3>
          <div className="space-y-3">
            {subs.map((s) => (
              <div key={s.id} className="paper-card p-5 flex items-start justify-between gap-4" data-testid={`my-submission-${s.id}`}>
                <div>
                  <p className="font-serif text-lg text-ink leading-tight">{s.title}</p>
                  <p className="font-mono text-xs text-stone mt-1">
                    {s.type === "upload" ? "PDF attached" : "Request"} · {formatDate(s.created_at)}
                  </p>
                  {s.note && <p className="font-sans text-sm text-ink/70 mt-2">{s.note}</p>}
                </div>
                <StatusMark status={s.status} />
              </div>
            ))}
            {subs.length === 0 && <p className="font-sans text-sm text-stone">No proposals yet.</p>}
          </div>
        </div>

        <div>
          <h3 className="font-serif text-2xl text-ink mb-4">My questions & updates</h3>
          <div className="space-y-3">
            {msgs.map((m) => (
              <div key={m.id} className="paper-card p-5 flex items-start justify-between gap-4" data-testid={`my-message-${m.id}`}>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-brass">{m.kind}</p>
                  <p className="font-sans text-sm text-ink mt-1">{m.body}</p>
                  <p className="font-mono text-xs text-stone mt-1">{formatDate(m.created_at)}{m.class_title ? ` · ${m.class_title}` : ""}</p>
                </div>
                <StatusMark status={m.status === "resolved" ? "approved" : "scheduled"} labelOverride={m.status === "resolved" ? "Seen" : "Open"} />
              </div>
            ))}
            {msgs.length === 0 && <p className="font-sans text-sm text-stone">Nothing sent yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
